import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Video, PhoneOff, Loader2, Phone, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * VideoConsultation - Daily.co iframe approach
 * Uses Daily.co Prebuilt UI embedded in an iframe for reliable video calls
 */
function VideoConsultation({ appointmentId, userId, userName, userRole, onEnd, onNoteGenerated }) {
  const [callState, setCallState] = useState('init'); // init, loading, polling, connected, error
  const [callDuration, setCallDuration] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingStatus, setRecordingStatus] = useState('');
  const [roomUrl, setRoomUrl] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [pollingAttempts, setPollingAttempts] = useState(0);
  // Disconnect handling: distinguishes accidental drop vs voluntary hangup
  const [disconnectState, setDisconnectState] = useState(null); // null | 'reconnecting' | 'lost'
  const [iframeReloadKey, setIframeReloadKey] = useState(0);
  const voluntaryEndRef = useRef(false);
  const disconnectTimerRef = useRef(null);

  const iframeRef = useRef(null);
  const timerRef = useRef(null);
  const isMountedRef = useRef(true);
  const pollingRef = useRef(null);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioStreamRef = useRef(null);

  const formatTime = useCallback((seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }, []);

  const getAuthToken = useCallback(() => {
    return userRole === 'doctor' 
      ? localStorage.getItem('doctor_token') 
      : localStorage.getItem('auth_token');
  }, [userRole]);

  // NOTE: Automatic recording during the call is intentionally disabled.
  // Doctors dictate their consultation summary AFTER the call via the
  // "Créer Note" modal (ConsultationNotes.js) which uses the microphone
  // without competing with the Daily.co iframe.

  // Create room (doctor only)
  const createRoom = useCallback(async () => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${API_URL}/api/video/room/create?appointment_id=${appointmentId}`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Échec création salle');
      }
      
      const data = await response.json();
      console.log('[Daily.co] Room created:', data);
      return data.room_url;
    } catch (err) {
      console.error('[Daily.co] Create room error:', err);
      throw err;
    }
  }, [appointmentId, getAuthToken]);

  // Poll for room URL (patient)
  const pollForRoom = useCallback(async () => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${API_URL}/api/video/room/join/${appointmentId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.room_url) {
          return data.room_url;
        }
      }
      return null;
    } catch (err) {
      console.error('[Daily.co] Poll error:', err);
      return null;
    }
  }, [appointmentId, getAuthToken]);

  // Start call
  const startCall = useCallback(async () => {
    setCallState('loading');
    setErrorMessage('');
    
    try {
      if (userRole === 'doctor') {
        // Doctor creates the room
        const url = await createRoom();
        if (url) {
          setRoomUrl(url);
          setCallState('connected');
        } else {
          throw new Error('URL de salle non reçue');
        }
      } else {
        // Patient polls for the room
        setCallState('polling');
        setPollingAttempts(0);
        
        // Start polling
        pollingRef.current = setInterval(async () => {
          if (!isMountedRef.current) {
            clearInterval(pollingRef.current);
            return;
          }
          
          setPollingAttempts(prev => prev + 1);
          const url = await pollForRoom();
          
          if (url) {
            clearInterval(pollingRef.current);
            setRoomUrl(url);
            setCallState('connected');
          }
        }, 3000); // Poll every 3 seconds
      }
    } catch (err) {
      console.error('[Call] Start error:', err);
      setErrorMessage(err.message || 'Erreur de connexion');
      setCallState('error');
    }
  }, [userRole, createRoom, pollForRoom]);

  // Handle iframe load
  const handleIframeLoad = useCallback(() => {
    console.log('[Daily.co] Iframe loaded');

    // Start timer
    timerRef.current = setInterval(() => {
      if (isMountedRef.current) setCallDuration(d => d + 1);
    }, 1000);

    // NOTE: Auto-recording during the call is disabled — the browser cannot
    // reliably capture microphone audio while the Daily.co iframe holds it
    // (only the doctor's voice is captured, not the patient's). The doctor
    // can dictate the consultation summary AFTER the call via the
    // "Créer Note" modal (ConsultationNotes), which yields a much more
    // accurate transcription.
  }, []);

  // End call
  const handleEndCall = useCallback(async () => {
    console.log('[Call] Ending (voluntary)...');
    voluntaryEndRef.current = true;

    // Stop polling if active
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    // Release any audio stream that might have been opened previously
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(t => t.stop());
    }

    // End room on backend
    try {
      const token = getAuthToken();
      await fetch(`${API_URL}/api/video/room/end/${appointmentId}?duration_seconds=${callDuration}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (err) {
      console.error('[Call] End error:', err);
    }

    setRecordingStatus('');
    onEnd();
  }, [onEnd, getAuthToken, appointmentId, callDuration]);

  // Cleanup
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (timerRef.current) clearInterval(timerRef.current);
      if (pollingRef.current) clearInterval(pollingRef.current);
      if (disconnectTimerRef.current) clearTimeout(disconnectTimerRef.current);
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(t => t.stop());
      }
    };
  }, []);

  // Listen to Daily.co iframe events via postMessage to detect accidental disconnects
  // (so we can show OUR overlay instead of Daily.co's raw "contact organizer" message,
  // which conflicts with the new WhatsApp link that the doctor may have just re-sent).
  useEffect(() => {
    const onMessage = (event) => {
      // Daily.co posts from https://*.daily.co
      if (!event.origin || !event.origin.includes('daily.co')) return;
      const data = event.data || {};
      const action = data.action || data.type;
      if (!action) return;
      // eslint-disable-next-line no-console
      console.log('[Daily.co event]', action, data);

      // A participant joined — clear any reconnection overlay
      if (action === 'joined-meeting' || action === 'participant-joined') {
        if (disconnectTimerRef.current) {
          clearTimeout(disconnectTimerRef.current);
          disconnectTimerRef.current = null;
        }
        setDisconnectState(null);
      }

      // The user left the meeting (voluntary via our End button OR Daily.co UI OR network drop)
      if (action === 'left-meeting') {
        if (voluntaryEndRef.current) return; // user clicked End — normal flow
        // Accidental disconnect — start a 30s reconnection window
        setDisconnectState('reconnecting');
        if (disconnectTimerRef.current) clearTimeout(disconnectTimerRef.current);
        disconnectTimerRef.current = setTimeout(() => {
          if (isMountedRef.current) setDisconnectState('lost');
        }, 30000);
      }

      // Fatal error inside Daily.co (bad room, network...)
      if (action === 'error') {
        if (voluntaryEndRef.current) return;
        setDisconnectState('lost');
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  const handleRetryConnection = useCallback(() => {
    // Force iframe reload with same room URL (Daily.co room TTL is 2h, so it's still valid)
    setDisconnectState('reconnecting');
    setIframeReloadKey(k => k + 1);
    if (disconnectTimerRef.current) clearTimeout(disconnectTimerRef.current);
    disconnectTimerRef.current = setTimeout(() => {
      if (isMountedRef.current) setDisconnectState('lost');
    }, 30000);
  }, []);

  // Init screen
  if (callState === 'init') {
    return (
      <div className="video-consultation-permission" data-testid="video-init">
        <div className="permission-icon"><Video size={64} /></div>
        <h2>Téléconsultation</h2>
        <p style={{ maxWidth: '350px', lineHeight: 1.6 }}>
          Consultation vidéo avec votre {userRole === 'doctor' ? 'patient' : 'médecin'}.
        </p>
        <p style={{ fontSize: '0.9rem', color: '#4A90A4', marginTop: '0.5rem', fontWeight: '500' }}>
          Powered by Daily.co
        </p>
        <button onClick={startCall} className="btn-authorize" data-testid="start-call-btn">
          <Phone size={20} /> {userRole === 'doctor' ? 'Démarrer la consultation' : 'Rejoindre la consultation'}
        </button>
        <button onClick={onEnd} className="btn-cancel-permission">Annuler</button>
      </div>
    );
  }

  // Error screen
  if (callState === 'error') {
    return (
      <div className="video-consultation-permission" data-testid="video-error">
        <div className="permission-icon" style={{ color: '#ef4444' }}><Video size={64} /></div>
        <h2>Erreur de connexion</h2>
        <p style={{ maxWidth: '350px', lineHeight: 1.6, color: '#ef4444' }}>
          {errorMessage || 'Impossible de se connecter à la salle vidéo.'}
        </p>
        <button onClick={startCall} className="btn-authorize" data-testid="retry-btn">
          <RefreshCw size={20} /> Réessayer
        </button>
        <button onClick={onEnd} className="btn-cancel-permission">Annuler</button>
      </div>
    );
  }

  // Loading screen (doctor creating room)
  if (callState === 'loading') {
    return (
      <div className="video-call-container" data-testid="video-loading">
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#1a1a2e'
        }}>
          <Loader2 size={48} className="animate-spin" style={{ color: '#4A90A4' }} />
          <h2 style={{ marginTop: '1rem', color: 'white' }}>Création de la salle...</h2>
          <p style={{ color: '#888' }}>Préparation de la consultation vidéo</p>
          
          <button 
            onClick={onEnd}
            style={{
              marginTop: '2rem',
              padding: '0.75rem 2rem',
              background: 'rgba(239, 68, 68, 0.8)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          >
            Annuler
          </button>
        </div>
      </div>
    );
  }

  // Polling screen (patient waiting for doctor)
  if (callState === 'polling') {
    return (
      <div className="video-call-container" data-testid="video-polling">
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#1a1a2e'
        }}>
          <Loader2 size={48} className="animate-spin" style={{ color: '#4A90A4' }} />
          <h2 style={{ marginTop: '1rem', color: 'white' }}>En attente du médecin...</h2>
          <p style={{ color: '#888' }}>Le médecin doit d&apos;abord démarrer la consultation</p>
          <p style={{ color: '#666', fontSize: '0.85rem', marginTop: '0.5rem' }}>
            Vérification... ({pollingAttempts})
          </p>
          
          <button 
            onClick={() => {
              if (pollingRef.current) clearInterval(pollingRef.current);
              onEnd();
            }}
            style={{
              marginTop: '2rem',
              padding: '0.75rem 2rem',
              background: 'rgba(239, 68, 68, 0.8)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          >
            Annuler
          </button>
        </div>
      </div>
    );
  }

  // Connected - show Daily.co iframe
  return (
    <div className="video-call-container" data-testid="video-call">
      {/* Daily.co Prebuilt iframe */}
      <iframe
        key={iframeReloadKey}
        ref={iframeRef}
        src={roomUrl}
        onLoad={handleIframeLoad}
        style={{
          width: '100%',
          height: '100%',
          border: 'none'
        }}
        allow="camera; microphone; fullscreen; display-capture; autoplay"
        title="Téléconsultation Daily.co"
      />

      {/* Custom disconnect overlay: hides Daily.co's default "contact organizer"
          screen and gives the patient a clear reconnect path aligned with the
          new WhatsApp link the doctor may be re-sending in parallel. */}
      {disconnectState && (
        <div
          data-testid="video-disconnect-overlay"
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.94)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 20,
            padding: '2rem',
            textAlign: 'center',
            color: 'white',
          }}
        >
          {disconnectState === 'reconnecting' ? (
            <>
              <Loader2 size={48} className="animate-spin" style={{ marginBottom: 16, color: '#60A5FA' }} />
              <h2 style={{ margin: 0, fontSize: '1.35rem' }}>Reconnexion en cours…</h2>
              <p style={{ maxWidth: 420, marginTop: 12, color: '#CBD5E1', lineHeight: 1.5 }}>
                La connexion à l'appel a été interrompue. Nous essayons de vous reconnecter automatiquement.
                Si votre médecin a relancé l'appel, cliquez sur le nouveau lien WhatsApp — c'est le même appel.
              </p>
              <button
                onClick={handleRetryConnection}
                data-testid="video-retry-btn"
                style={{
                  marginTop: 20, padding: '10px 20px', background: '#3B82F6', color: 'white',
                  border: 0, borderRadius: 8, display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.95rem',
                }}
              >
                <RefreshCw size={18} /> Réessayer maintenant
              </button>
            </>
          ) : (
            <>
              <PhoneOff size={48} style={{ marginBottom: 16, color: '#F87171' }} />
              <h2 style={{ margin: 0, fontSize: '1.35rem' }}>Connexion perdue</h2>
              <p style={{ maxWidth: 420, marginTop: 12, color: '#CBD5E1', lineHeight: 1.5 }}>
                Nous n'avons pas pu vous reconnecter automatiquement. Votre médecin est peut-être en train de relancer l'appel.
                <strong style={{ display: 'block', marginTop: 8, color: 'white' }}>
                  Vérifiez votre WhatsApp — un nouveau lien peut avoir été envoyé.
                </strong>
              </p>
              <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
                <button
                  onClick={handleRetryConnection}
                  data-testid="video-retry-btn"
                  style={{
                    padding: '10px 20px', background: '#3B82F6', color: 'white', border: 0, borderRadius: 8,
                    display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.95rem',
                  }}
                >
                  <RefreshCw size={18} /> Rejoindre à nouveau
                </button>
                <button
                  onClick={() => { voluntaryEndRef.current = true; onEnd(); }}
                  data-testid="video-close-btn"
                  style={{
                    padding: '10px 20px', background: 'transparent', color: '#CBD5E1', border: '1px solid #475569', borderRadius: 8, cursor: 'pointer', fontSize: '0.95rem',
                  }}
                >
                  Fermer
                </button>
              </div>
            </>
          )}
        </div>
      )}
      
      {/* Timer overlay */}
      <div style={{
        position: 'absolute',
        top: '1rem',
        left: '1rem',
        display: 'flex',
        gap: '0.75rem',
        zIndex: 10
      }}>
        <span className="call-timer">{formatTime(callDuration)}</span>
        {isRecording && <span className="rec-badge"><span className="rec-dot"></span> REC</span>}
      </div>
      
      {/* End call button */}
      <button 
        onClick={handleEndCall}
        data-testid="end-call-btn"
        style={{
          position: 'absolute',
          bottom: '1.5rem',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '64px',
          height: '64px',
          borderRadius: '50%',
          background: '#ef4444',
          color: 'white',
          border: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          zIndex: 10,
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
        }}
      >
        <PhoneOff size={28} />
      </button>

      {/* Processing overlay */}
      {recordingStatus === 'processing' && (
        <div className="processing-overlay">
          <Loader2 size={32} className="animate-spin" />
          <p>Génération des notes...</p>
        </div>
      )}
    </div>
  );
}

export default VideoConsultation;
